import { useQuery } from '@tanstack/react-query'
import { type Task, type TaskInput, type TaskPersonOption, type TaskResultInput, taskWaiting } from '@tnn/domain'
import { api } from '../api/client'

/** Tasks as the API serves them (`/api/me/tasks`, modules/tasks) */

export const tasksKey = ['me', 'tasks'] as const

export const tasksApi = {
  list: () => api.get<{ assigned: Task[]; sent: Task[] }>('/me/tasks'),
  get: (id: string) => api.get<{ item: Task }>(`/me/tasks/${encodeURIComponent(id)}`).then((r) => r.item),
  create: (input: TaskInput) => api.post<{ item: Task }>('/me/tasks', input).then((r) => r.item),
  cancel: (id: string) => api.post<{ item: Task }>(`/me/tasks/${encodeURIComponent(id)}/cancel`).then((r) => r.item),
  accept: (id: string, always = false) =>
    api.post<{ item: Task }>(`/me/tasks/${encodeURIComponent(id)}/accept`, { always }).then((r) => r.item),
  decline: (id: string, never = false) =>
    api.post<{ item: Task }>(`/me/tasks/${encodeURIComponent(id)}/decline`, { never }).then((r) => r.item),
  done: (id: string, input: TaskResultInput) =>
    api.post<{ item: Task }>(`/me/tasks/${encodeURIComponent(id)}/done`, input).then((r) => r.item),
  people: (q: string) => api.get<{ items: TaskPersonOption[] }>(`/me/tasks/people?q=${encodeURIComponent(q)}`).then((r) => r.items),
  trust: () => api.get<{ items: TaskPersonOption[] }>('/me/tasks/trust').then((r) => r.items),
  setTrust: (userIds: string[]) => api.put<{ items: TaskPersonOption[] }>('/me/tasks/trust', { userIds }).then((r) => r.items),
}

/** Mine to do and what I asked; checked again every half minute while the page is open */
export function useMyTasks(enabled = true) {
  return useQuery({ queryKey: tasksKey, queryFn: tasksApi.list, enabled, refetchInterval: 30_000 })
}

/**
 * One task, while it waits for its doer (the field it fills is on screen): fetched again the moment it changes
 * (`useLiveEvents`), and every quarter minute in case that was missed
 */
export function useTask(id: string | null | undefined) {
  return useQuery({
    queryKey: [...tasksKey, id],
    queryFn: () => tasksApi.get(String(id)),
    enabled: !!id,
    refetchInterval: (query) => (query.state.data && !taskWaiting(query.state.data.status) ? false : 15_000),
  })
}
